import { useEffect, useState } from "react";
import type { ChangeEvent, DragEvent, KeyboardEvent } from "react";

import heic2any from "heic2any";
import { User } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@components/Avatar";
import { Button } from "@components/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/Dialog";
import { FileUpload } from "@components/FileUpload";
import { Image } from "@components/Image";
import { Link } from "@components/Link";
import { Separator } from "@components/Separator";
import { Spinner } from "@components/Spinner";
import { Stack } from "@components/Stack";
import { Textarea } from "@components/Textarea";
import { onToast } from "@components/Toast";
import { Typography } from "@components/Typography";

import type { TPost, TPostFilter } from "@api/modelCamPosts";

import { isShowPostSentModerationDialogStorage } from "@storage/localStorage";

import { cn } from "@lib/utils";

import { ASSETS_BASE_URL, ICON_PATHS } from "@configs/icons";

export type PostDialogPayload = {
	id?: string;
	image_url: File | string | null;
	body: string;
	sort_order?: number;
};

export type EditPostDialogProps = {
	id: string;
	action: string;
	imageUrl: string;
	post: TPost | null;
	onAddPost: (data: PostDialogPayload) => Promise<void>;
	onEditPost: (data: PostDialogPayload, statusPost: TPostFilter) => Promise<void>;
	nickname: string;
	statusPost: TPostFilter;
	onClose: () => void;
	onPostSentToModeration?: () => void;
	isPendingAddPost: boolean;
	isPendingEditPost: boolean;
};

type ImgAreaProps = {
	photoUrl: string | null;
	fullLoading: boolean;
};

const ACCEPT_IMAGES = "image/jpeg,image/png,.heic,.heif";

const notifyError = (description: string) => {
	onToast({ message: "Error", description });
};

const ImgArea = ({ photoUrl, fullLoading }: ImgAreaProps) => {
	if (fullLoading) {
		return (
			<Stack align="center" justify="center" wide spacing={10} className="h-75 rounded-lg bg-ground/20">
				<Spinner className="size-8" />
				<Typography type="p" size="sm" padding="none" color="subTitle">
					Loading...
				</Typography>
			</Stack>
		);
	}

	if (photoUrl) {
		return (
			<div className="h-full w-full overflow-hidden rounded-lg [&_picture]:block [&_picture]:h-full [&_picture]:w-full">
				<Image
					id="photo"
					width="100%"
					height="100%"
					src={photoUrl}
					alt="post"
					className="block h-full w-full object-cover"
				/>
			</div>
		);
	}

	return (
		<>
			<Image width={50} height={50} src={ICON_PATHS.ADD_A_PHOTO} alt="upload" />
			<Typography type="p" padding="none" weight="bold">
				click to select photo
			</Typography>
			<Typography type="p" size="sm" padding="none">
				or drag file here
			</Typography>
		</>
	);
};

export const EditPostDialog = ({
	id,
	action,
	imageUrl,
	nickname,
	post,
	onAddPost,
	onEditPost,
	statusPost,
	onClose,
	onPostSentToModeration,
	isPendingAddPost,
	isPendingEditPost,
}: EditPostDialogProps) => {
	const [file, setFile] = useState<File | null>(null);
	const [photoUrl, setPhotoUrl] = useState<string | null>(post?.image_url ?? null);
	const [body, setBody] = useState(post?.body ?? "");
	const [showAgainModal, setShowAgainModal] = useState<boolean | null>(null);
	const [textHeightRows, setTextHeightRows] = useState(7);
	const [fullLoading, setFullLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const isDisabledPublishButton =
		isPendingAddPost || isPendingEditPost || isSubmitting || fullLoading || !(body || photoUrl);

	useEffect(() => {
		const isShowPostSentModerationDialog = isShowPostSentModerationDialogStorage.getItem();
		setShowAgainModal(isShowPostSentModerationDialog === "true");
	}, []);

	const isHeicFile = (file: File) => {
		const fileName = file.name;
		const extension = fileName.slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase();
		return extension === "heic" || extension === "heif";
	};

	const convertHeicToJpeg = (heicFile: File): Promise<File> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onerror = function () {
				reject("ERR_DOM Error on converting blob to data URL!!!");
			};
			reader.onload = () => {
				const heicBlob = new Blob([reader.result as ArrayBuffer]);
				heic2any({
					blob: heicBlob,
					toType: "image/jpeg",
					quality: 0.7,
				})
					.then((jpegBlob) => {
						const blob = Array.isArray(jpegBlob) ? jpegBlob[0] : jpegBlob;
						const jpegFile = new File([blob], `${heicFile.name}.jpeg`, {
							type: blob.type,
						});
						resolve(jpegFile);
					})
					.catch((error) => reject(error));
			};
			reader.readAsArrayBuffer(heicFile);
		});

	const checkImageDimensions = (file: File): Promise<boolean> =>
		new Promise((resolve) => {
			const img = new window.Image();
			const objectUrl = URL.createObjectURL(file);

			img.onload = () => {
				const isWithinSize = img.width >= 480 && img.height >= 480;
				URL.revokeObjectURL(objectUrl);
				resolve(isWithinSize);
			};
			img.onerror = () => {
				URL.revokeObjectURL(objectUrl);
				resolve(false);
			};
			img.src = objectUrl;
		});

	const beforeUpload = async (selectedFile: File): Promise<boolean> => {
		setFullLoading(true);

		let checkedFile = selectedFile;
		if (isHeicFile(checkedFile)) {
			try {
				checkedFile = await convertHeicToJpeg(checkedFile);
			} catch (error) {
				console.error(error);
				notifyError("File is corrupt");
				setFullLoading(false);
				return false;
			}
		}

		const isJpgOrPng = checkedFile.type === "image/jpeg" || checkedFile.type === "image/png";
		const isLess20Mb = checkedFile.size / 1024 / 1024 < 20;
		const isWithinSize = await checkImageDimensions(checkedFile);

		if (!isJpgOrPng) {
			notifyError("Only JPG, JPEG, HEIC and PNG file formats are allowed");
		}
		if (!isLess20Mb) {
			notifyError("File size should not exceed 20Mb");
		}
		if (isJpgOrPng && !isWithinSize) {
			notifyError("File must have a minimum resolution of 480x480 pixels");
		}

		const isValid = isJpgOrPng && isLess20Mb && isWithinSize;
		if (!isValid) {
			setFullLoading(false);
		}

		return isValid;
	};

	const onFileChange = async (selectedFile: File | null) => {
		if (!selectedFile) {
			setFullLoading(false);
			return;
		}

		let checkedFile = selectedFile;
		if (isHeicFile(checkedFile)) {
			try {
				checkedFile = await convertHeicToJpeg(checkedFile);
			} catch (error) {
				console.error(error);
				notifyError("File is corrupt");
				setFullLoading(false);
				return;
			}
		}

		const src = await new Promise<string>((resolve) => {
			const reader = new FileReader();
			reader.readAsDataURL(checkedFile);
			reader.onload = () => resolve(reader.result as string);
		});

		setFile(checkedFile);
		setPhotoUrl(src);
		setFullLoading(false);
	};

	const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();

		if (fullLoading) {
			return;
		}

		const selectedFile = event.dataTransfer.files[0];
		if (!selectedFile) {
			return;
		}

		const shouldUpload = await beforeUpload(selectedFile);
		if (shouldUpload) {
			await onFileChange(selectedFile);
		}
	};

	const handleOk = () => {
		onClose();
	};

	const handleCancel = () => {
		if (fullLoading) return;
		if (id) {
			setBody(post?.body ?? "");
			setPhotoUrl(post?.image_url ?? null);
		} else {
			setBody("");
			setPhotoUrl(null);
			setFullLoading(false);
			setFile(null);
		}
		onClose();
	};

	const handlePostDialogOpenChange = (open: boolean) => {
		if (!open) {
			handleCancel();
		}
	};

	// const handleClose = () => {
	// 	setPhotoUrl(null);
	// 	setFile(null);
	// };

	const handleChangeBody = (e: ChangeEvent<HTMLTextAreaElement>) => {
		setBody(e.target.value);
	};

	const handleKeydown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
		if (!id) {
			return;
		}

		const input = e.currentTarget;
		let position = input.selectionStart;
		if (e.code === "ArrowRight") {
			position++;
		} else if (e.code === "ArrowLeft") {
			position--;
		} else if (e.code === "ArrowUp") {
			position -= 40;
		} else if (e.code === "ArrowDown") {
			position += 40;
		}

		const nextPosition = Math.max(0, Math.min(position, input.value.length));
		input.setSelectionRange(nextPosition, nextPosition);
		input.focus();
	};

	const handleChange = async () => {
		if (isSubmitting || (id && !post)) {
			return;
		}

		setIsSubmitting(true);

		try {
			if (!id) {
				const data = {
					image_url: file,
					body,
				};

				await onAddPost(data);
				setBody("");
				setPhotoUrl(null);
				setFile(null);
				setTextHeightRows(7);
			} else if (post) {
				const data = {
					id: post.id,
					image_url: file || post.image_url,
					body,
					sort_order: post.sort_order,
				};

				await onEditPost(data, statusPost);
				setTextHeightRows(7);
			}
		} catch {
			setIsSubmitting(false);
			return;
		}

		setIsSubmitting(false);
		handleOk();
		if (!showAgainModal) {
			onPostSentToModeration?.();
		}
	};

	return (
		<Dialog open onOpenChange={handlePostDialogOpenChange}>
			<DialogContent
				className={`chat-screen-scrollbar max-h-[calc(100dvh-2rem)] max-w-170.75 overflow-y-auto overscroll-contain
					pb-9`}
			>
				<DialogHeader>
					<DialogTitle>{action}</DialogTitle>
				</DialogHeader>

				<Stack direction="row" spacing={20} align="stretch" wide>
					<Stack wide>
						{/** Logic to show image for full screen */}
						{/* {photoUrl && !fullLoading ? (
							<button
								type="button"
								className="absolute right-0 top-0 z-999 h-[13%] w-[17%] cursor-pointer border-0 bg-transparent p-0"
								onClick={(event) => {
									event.stopPropagation();
									handleClose();
								}}
							>
								<Image
									width={24}
									height={24}
									className="absolute right-[22px] top-[13px] z-9999 text-xl hover:opacity-60"
									src={ICON_PATHS.CLOSE}
									alt="close"
								/>
							</button>
						) : null} */}
						<FileUpload
							accept={ACCEPT_IMAGES}
							onBeforeSelect={beforeUpload}
							onChange={onFileChange}
							disabled={fullLoading}
							className="relative z-docked w-full"
						>
							<Stack
								align="center"
								justify="center"
								spacing={8}
								className={cn(
									"h-75 w-full cursor-pointer overflow-hidden rounded-lg bg-ground/20",
									!photoUrl ? "bg-secondary" : "",
								)}
								onDrop={handleDrop}
								onDragOver={(event) => event.preventDefault()}
							>
								<ImgArea photoUrl={photoUrl} fullLoading={fullLoading} />
							</Stack>
						</FileUpload>
					</Stack>

					<Stack wide>
						<Stack direction="row" align="center" spacing={16} className="mb-4">
							<Avatar size="lg">
								<AvatarImage src={imageUrl || undefined} alt="Avatar" />
								<AvatarFallback className="bg-gray-600 opacity-100">
									<User className="size-7 text-white" strokeWidth={1.5} />
								</AvatarFallback>
							</Avatar>
							<Typography type="p" weight="bold" padding="none" className="max-w-53.75 truncate">
								{nickname}
							</Typography>
						</Stack>
						<div className="invisible-scrollbar relative mb-5 w-full">
							<Textarea
								id={id}
								className="border-0 px-0 hover:border-0 focus:border-0 focus-visible:border-0"
								rows={textHeightRows}
								maxLength={1024}
								value={body}
								showCount
								isResize
								placeholder="Type your text here"
								aria-label="Post text"
								onChange={handleChangeBody}
								onKeyDown={handleKeydown}
							/>
							<Separator className="absolute bottom-0 m-0" />
						</div>
						<div className="mt-auto w-full">
							<Button
								onClick={handleChange}
								type={isDisabledPublishButton ? "secondary" : "primary"}
								wide
								disable={isDisabledPublishButton}
							>
								<Typography type="p" padding="none" weight="bold" size="sm">
									{action === "Create post" ? "Publish" : "Save changes"}
								</Typography>
							</Button>
						</div>
					</Stack>
				</Stack>

				<div className="mt-5">
					<Typography type="p" color="subTitle" padding="none" size="xs">
						Dreamcam allows models to upload content. All models must comply with our policy for uploading files. Models
						are solely responsible for the content that they upload. By Clicking Publish you agree to our{" "}
						<Link
							href={`${ASSETS_BASE_URL}/TERMS%20OF%20SERVICE.pdf`}
							target="_blank"
							rel="noreferrer"
							className="inline-block hover:text-white hover:opacity-60"
							underline="none"
						>
							<Typography size="xs" type="span" padding="none">
								TERMS OF USE.
							</Typography>
						</Link>
					</Typography>
				</div>
			</DialogContent>
		</Dialog>
	);
};
